import { queryOptions, useQuery } from '@tanstack/react-query';
import { api, expectBody } from '../api/client.js';
import type { Principal, ScopedPermission } from './permissions.js';

/** The signed-in user, their permissions with scopes, and their locale and theme. */
export interface CurrentUser extends Principal {
  readonly displayName: string;
  readonly email: string;
  readonly sessionId: string;
  /** Bumped by every role change, so a stale permission set can be noticed and replaced. */
  readonly rolesVersion: number;
  readonly theme: 'system' | 'light' | 'dark';
  readonly locale: string | undefined;
  readonly roles: readonly { code: string; scopeType: string; scopeIds: readonly string[] }[];
}

export const meQueryKey = ['me'] as const;

/**
 * `/v1/me`, as a query.
 *
 * It is server state like any other and lives in the cache rather than in a store, so that one
 * copy of "who am I" goes stale instead of two. `tenant:config` and a role change both arrive as
 * events, and both repair it by invalidating this key.
 */
export function meQueryOptions() {
  return queryOptions({
    queryKey: meQueryKey,
    queryFn: async (): Promise<CurrentUser> => {
      const me = expectBody(await api.GET('/v1/me'));
      return {
        userId: me.user.id,
        displayName: me.user.displayName,
        email: me.user.email,
        sessionId: me.sessionId,
        rolesVersion: me.user.rolesVersion,
        theme: me.user.theme,
        locale: me.user.locale,
        permissions: me.permissions as readonly ScopedPermission[],
        roles: me.roles,
      };
    },
    // Permissions are not something to re-read every half minute: they change when a role changes,
    // which is an event, and the event is what invalidates this.
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useCurrentUser() {
  return useQuery(meQueryOptions());
}
